import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Container,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from 'react-router-dom';
import { z } from 'zod';

import * as pwApi from '@/api/passwordReset';

const Schema = z.object({ email: z.string().email() });
type Input = z.infer<typeof Schema>;

export function PasswordResetRequestPage() {
  const [done, setDone] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<Input>({ resolver: zodResolver(Schema) });

  const onSubmit = handleSubmit(async (values) => {
    await pwApi.requestReset(values.email).catch(() => undefined);
    setDone(true);
  });

  return (
    <Container maxWidth="xs" sx={{ pt: 8 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Reset your password
      </Typography>
      {done ? (
        <Stack spacing={2}>
          <Alert severity="success">
            If an account exists for that email, a reset link has been sent.
          </Alert>
          <MuiLink component={RouterLink} to="/auth/login" underline="hover">
            Back to sign-in
          </MuiLink>
        </Stack>
      ) : (
        <Box component="form" onSubmit={onSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              autoComplete="username"
              autoFocus
              error={Boolean(errors.email)}
              helperText={errors.email?.message}
              {...register('email')}
            />
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? 'Sending…' : 'Send reset link'}
            </Button>
            <MuiLink component={RouterLink} to="/auth/login" underline="hover">
              Back to sign-in
            </MuiLink>
          </Stack>
        </Box>
      )}
    </Container>
  );
}
